import {
  Color,
  DepthTexture,
  HalfFloatType,
  LinearFilter,
  Matrix4,
  NoToneMapping,
  PCFShadowMap,
  RepeatWrapping,
  UnsignedByteType,
  Vector2,
  Vector3,
  WebGL3DRenderTarget,
  WebGLRenderTarget,
  WebGLRenderer,
} from 'three';
import type { Camera, PerspectiveCamera, Scene, Texture, TextureDataType } from 'three';
import type { MapDef, QualityPreset } from '@cloudpearl/config';
import { BLOOM_PREFILTER_FS, CLOUD_NOISE_FS, CLOUDS_FS, FINAL_FS, KAWASE_DOWN_FS, KAWASE_UP_FS, SHAFTS_FS } from '@cloudpearl/assets';
import type { SharedUniforms } from '@cloudpearl/assets';
import { FullscreenPass } from './FullscreenPass';

export interface PostParams {
  speed: number;
  /** Pearl position on screen (uv) and radius — excluded from the speed blur. */
  hero: Vector3;
  focus: Vector2;
  storm: number;
  flash: number;
  flashColor: Color;
  fade: number;
  desat: number;
}

/**
 * Owns the WebGL context, the HDR scene target, volumetric clouds, bloom and the final
 * "speed" composite. Also runs dynamic resolution so frame time stays on budget everywhere.
 */
export class Renderer {
  readonly gl: WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  private quality: QualityPreset;
  private shared: SharedUniforms;
  private map: MapDef | null = null;

  private sceneRT!: WebGLRenderTarget;
  private cloudRT: WebGLRenderTarget | null = null;
  private shaftRT: WebGLRenderTarget | null = null;
  private shaftPass: FullscreenPass | null = null;
  private readonly sunNdc = new Vector3();
  private readonly camDir = new Vector3();
  private bloomDown: WebGLRenderTarget[] = [];
  private bloomUp: WebGLRenderTarget[] = [];
  private cloudNoise: WebGL3DRenderTarget | null = null;

  private cloudPass: FullscreenPass | null = null;
  private prefilter!: FullscreenPass;
  private down!: FullscreenPass;
  private up!: FullscreenPass;
  private final!: FullscreenPass;

  private readonly hdrType: TextureDataType;
  private renderScale = 1;
  private frame = 0;
  private slowFrames = 0;
  private fastFrames = 0;
  private upPatience = 480;
  private lastStepUp = -1e9;
  private settleUntil = 0;
  private readonly size = new Vector2();
  private readonly invProj = new Matrix4();
  private windOffset = new Vector2();

  constructor(canvas: HTMLCanvasElement, quality: QualityPreset, shared: SharedUniforms) {
    this.canvas = canvas;
    this.quality = quality;
    this.shared = shared;
    this.gl = new WebGLRenderer({ canvas, antialias: false, alpha: false, stencil: false, powerPreference: 'high-performance' });
    this.gl.toneMapping = NoToneMapping;
    this.gl.info.autoReset = false;
    this.gl.autoClear = true;
    this.gl.setClearColor(0x000000, 1);
    this.gl.shadowMap.type = PCFShadowMap;
    this.gl.shadowMap.enabled = quality.shadows;
    const ext = this.gl.extensions;
    this.hdrType = ext.has('EXT_color_buffer_half_float') || ext.has('EXT_color_buffer_float') ? HalfFloatType : UnsignedByteType;
    this.renderScale = 1;
    this.buildTargets();
    this.buildPasses();
  }

  get pixelRatio(): number {
    const css = Math.max(1, this.canvas.clientWidth * this.canvas.clientHeight);
    const budget = Math.sqrt(this.quality.maxPixels / css);
    return Math.min(window.devicePixelRatio || 1, this.quality.maxPixelRatio, budget) * this.renderScale;
  }

  setQuality(q: QualityPreset): void {
    this.quality = q;
    this.gl.shadowMap.enabled = q.shadows;
    this.renderScale = 1;
    this.buildTargets();
    this.buildPasses();
    if (this.map) this.setMap(this.map);
  }

  setMap(map: MapDef): void {
    this.map = map;
    const c = map.clouds;
    if (c.enabled && !this.cloudNoise) this.cloudNoise = generateCloudNoise(this.gl, 64);
    if (c.enabled) {
      this.cloudPass?.dispose();
      this.cloudPass = new FullscreenPass(
        CLOUDS_FS,
        {
          ...this.shared.all(),
          tDepth: { value: this.sceneRT.depthTexture },
          tNoise: { value: this.cloudNoise!.texture },
          uInvProj: { value: new Matrix4() },
          uCamWorld: { value: new Matrix4() },
          uCamNearFar: { value: new Vector2(0.1, 3000) },
          uCloudSlab: { value: [c.bottom, c.top, c.coverage, c.density] },
          uCloudScale: { value: [c.scale, c.detailScale, 0, 0] },
          uAmbTop: { value: new Color(c.ambientTop) },
          uAmbBottom: { value: new Color(c.ambientBottom) },
          uFrame: { value: 0 },
        },
        { STEPS: this.quality.cloudSteps, LIGHT_STEPS: this.quality.cloudLightSteps },
      );
    } else {
      this.cloudPass?.dispose();
      this.cloudPass = null;
    }
    this.buildPasses();
    const f = this.final.uniforms;
    const l = map.lighting;
    f['uExposure']!.value = l.exposure;
    f['uSaturation']!.value = l.saturation;
    f['uContrast']!.value = l.contrast;
    (f['uGradeShadows']!.value as Color).set(l.gradeShadows).lerp(new Color(1, 1, 1), 0.78);
    (f['uGradeHighlights']!.value as Color).set(l.gradeHighlights);
    (f['uStormColor']!.value as Color).set(map.chaser.color);
    (f['uStormGlow']!.value as Color).set(map.chaser.glow);
    f['uStormKind']!.value = map.id === 'dunes' ? 1 : 0;
  }

  private buildTargets(): void {
    this.sceneRT?.dispose();
    this.cloudRT?.dispose();
    this.shaftRT?.dispose();
    for (const t of [...this.bloomDown, ...this.bloomUp]) t.dispose();
    const w = Math.max(2, Math.floor(this.canvas.clientWidth * this.pixelRatio));
    const h = Math.max(2, Math.floor(this.canvas.clientHeight * this.pixelRatio));
    this.size.set(w, h);
    const depth = new DepthTexture(w, h);
    this.sceneRT = new WebGLRenderTarget(w, h, { type: this.hdrType, samples: this.quality.msaa, depthTexture: depth, depthBuffer: true });
    const cs = this.quality.cloudScale;
    this.cloudRT = new WebGLRenderTarget(Math.max(2, Math.floor(w * cs)), Math.max(2, Math.floor(h * cs)), { type: this.hdrType, depthBuffer: false });
    this.shaftRT = new WebGLRenderTarget(Math.max(2, Math.floor(w / 4)), Math.max(2, Math.floor(h / 4)), { type: this.hdrType, depthBuffer: false });
    if (this.shaftPass) this.shaftPass.uniforms['tDepth']!.value = this.sceneRT.depthTexture;
    this.bloomDown = [];
    this.bloomUp = [];
    let bw = w;
    let bh = h;
    // Five levels (½ … 1/32): a wide, soft glow rather than tight halos.
    for (let i = 0; i < 5; i++) {
      bw = Math.max(2, bw >> 1);
      bh = Math.max(2, bh >> 1);
      this.bloomDown.push(new WebGLRenderTarget(bw, bh, { type: this.hdrType, depthBuffer: false }));
      this.bloomUp.push(new WebGLRenderTarget(bw, bh, { type: this.hdrType, depthBuffer: false }));
    }
    if (this.cloudPass) this.cloudPass.uniforms['tDepth']!.value = this.sceneRT.depthTexture;
  }

  private buildPasses(): void {
    const clouds = !!this.cloudPass;
    const defs: Record<string, string | number> = clouds ? { CLOUDS: 1 } : {};
    const sceneU = (): Record<string, { value: unknown }> => ({
      tScene: { value: this.sceneRT.texture },
      tClouds: { value: this.cloudRT?.texture ?? null },
    });
    this.prefilter?.dispose();
    this.down?.dispose();
    this.up?.dispose();
    const prevFinal = this.final?.uniforms;
    this.final?.dispose();
    this.prefilter = new FullscreenPass(BLOOM_PREFILTER_FS, { ...sceneU(), uTexel: { value: new Vector2() }, uThreshold: { value: 2.3 } }, defs);
    this.down = new FullscreenPass(KAWASE_DOWN_FS, { tInput: { value: null }, uTexel: { value: new Vector2() } });
    this.up = new FullscreenPass(KAWASE_UP_FS, { tInput: { value: null }, tBase: { value: null }, uTexel: { value: new Vector2() } });
    const finalDefs: Record<string, string | number> = { ...defs, TAPS: this.quality.speedBlurTaps };
    if (this.quality.bloom) finalDefs['BLOOM'] = 1;
    // Lens flare from the sun (same tiers as bloom).
    if (this.quality.bloom) finalDefs['FLARE'] = 1;
    const shafts = this.quality.level !== 'low' && (this.map?.atmosphere.shafts ?? 0) > 0;
    this.shaftPass?.dispose();
    this.shaftPass = shafts
      ? new FullscreenPass(SHAFTS_FS, { tDepth: { value: this.sceneRT.depthTexture }, uSunUv: { value: new Vector2(0.5, 0.5) }, uAspect: { value: 1 }, uFrame: { value: 0 } })
      : null;
    if (shafts) finalDefs['SHAFTS'] = 1;
    this.final = new FullscreenPass(
      FINAL_FS,
      {
        ...sceneU(),
        tBloom: { value: this.bloomUp[0]?.texture ?? null },
        uBloom: { value: 0.32 },
        uBloomTint: { value: new Color(1, 1, 1) },
        uGrain: { value: 0.015 },
        tDepth: { value: this.sceneRT.depthTexture },
        uFlare: { value: new Vector3(0.5, 0.5, 0) },
        uFlareColor: { value: new Color(1, 1, 1) },
        uResolution: { value: new Vector2() },
        uFocus: { value: new Vector2(0.5, 0.55) },
        uSpeed: { value: 0 },
        uTime: { value: 0 },
        uStorm: { value: 0 },
        uStormColor: { value: new Color() },
        uStormGlow: { value: new Color() },
        uStormKind: { value: 0 },
        uExposure: { value: 1 },
        uSaturation: { value: 1 },
        uContrast: { value: 1 },
        uGradeShadows: { value: new Color(1, 1, 1) },
        uGradeHighlights: { value: new Color(1, 1, 1) },
        uVignette: { value: 0.37 },
        uFlash: { value: 0 },
        uFlashColor: { value: new Color(1, 1, 1) },
        uFade: { value: 1 },
        uDesat: { value: 0 },
        uHero: { value: new Vector3(0.5, 0.3, 0.05) },
        tShafts: { value: this.shaftRT?.texture ?? null },
        uShaftColor: { value: new Color(0, 0, 0) },
      },
      finalDefs,
    );
    if (prevFinal) {
      for (const k of ['uExposure', 'uSaturation', 'uContrast', 'uGradeShadows', 'uGradeHighlights', 'uStormColor', 'uStormGlow', 'uStormKind']) {
        this.final.uniforms[k]!.value = prevFinal[k]!.value;
      }
    }
  }

  /** Call when the canvas CSS size changes. */
  resize(): void {
    this.gl.setPixelRatio(this.pixelRatio);
    this.gl.setSize(this.canvas.clientWidth, this.canvas.clientHeight, false);
    this.buildTargets();
    this.buildPasses();
  }

  /** Ignore frame-time spikes for a moment (loading, shader compiles, map switches). */
  settle(seconds = 1.5): void {
    this.settleUntil = performance.now() + seconds * 1000;
    this.slowFrames = 0;
    this.fastFrames = 0;
  }

  /**
   * Dynamic resolution. Feed it the real frame delta (seconds). Targets a 60 fps floor (not the
   * display's refresh — a 120 Hz panel running at 90 fps is fine), with hysteresis. Steps back up
   * after a run of on-time frames (under a 60 fps cap frames never arrive early, so "on time" is
   * the only signal); a step up that immediately has to be undone doubles the wait for the next.
   */
  adapt(dt: number): void {
    const now = performance.now();
    if (now < this.settleUntil) return;
    const budget = 1 / 60;
    if (dt > budget * 1.22) this.slowFrames++;
    else this.slowFrames = Math.max(0, this.slowFrames - 2);
    if (dt < budget * 1.08) this.fastFrames++;
    else this.fastFrames = Math.max(0, this.fastFrames - 4);
    if (this.slowFrames > 40 && this.renderScale > this.quality.minRenderScale) {
      if (now - this.lastStepUp < 8000) this.upPatience = Math.min(this.upPatience * 2, 7200);
      this.renderScale = Math.max(this.quality.minRenderScale, this.renderScale - 0.08);
      this.slowFrames = 0;
      this.fastFrames = 0;
      this.resize();
      this.settle(1);
    } else if (this.fastFrames > this.upPatience && this.renderScale < 1) {
      this.renderScale = Math.min(1, this.renderScale + 0.05);
      this.fastFrames = 0;
      this.lastStepUp = now;
      this.resize();
      this.settle(1);
    }
  }

  get scale(): number {
    return this.renderScale;
  }

  render(scene: Scene, camera: PerspectiveCamera, p: PostParams, time: number, dt: number): void {
    const gl = this.gl;
    this.frame++;
    gl.info.reset();
    gl.setRenderTarget(this.sceneRT);
    gl.render(scene, camera);

    if (this.cloudPass && this.map) {
      const u = this.cloudPass.uniforms;
      this.invProj.copy(camera.projectionMatrixInverse);
      (u['uInvProj']!.value as Matrix4).copy(this.invProj);
      (u['uCamWorld']!.value as Matrix4).copy(camera.matrixWorld);
      (u['uCamNearFar']!.value as Vector2).set(camera.near, camera.far);
      u['uFrame']!.value = this.frame % 64;
      const c = this.map.clouds;
      const w = this.shared.uWind.value;
      this.windOffset.x += w.x * c.windSpeed * dt;
      this.windOffset.y += w.y * c.windSpeed * dt;
      const cs = u['uCloudScale']!.value as number[];
      cs[2] = this.windOffset.x;
      cs[3] = this.windOffset.y;
      this.cloudPass.render(gl, this.cloudRT);
    }

    // Where's the sun on screen? (Shafts and the lens flare fade as it leaves the view.)
    const sun = this.shared.uSunDir.value;
    camera.getWorldDirection(this.camDir);
    const facing = this.camDir.dot(sun);
    this.sunNdc.copy(camera.position).addScaledVector(sun, 1000).project(camera);
    const off = Math.max(Math.abs(this.sunNdc.x), Math.abs(this.sunNdc.y));
    if (this.quality.bloom) {
      // A flare only while the sun itself is in frame (ghosts swing in as it nears the centre).
      const inFrame = smooth(0.3, 0.7, facing) * (1 - smooth(0.85, 1.05, off));
      (this.final.uniforms['uFlare']!.value as Vector3).set(this.sunNdc.x * 0.5 + 0.5, this.sunNdc.y * 0.5 + 0.5, inFrame * 0.35);
      (this.final.uniforms['uFlareColor']!.value as Color).copy(this.shared.uSunColor.value);
    }
    if (this.shaftPass && this.shaftRT && this.map) {
      // Shafts fade as the sun leaves the view or sinks behind the camera.
      const vis = smooth(-0.1, 0.45, facing) * (1 - smooth(1.1, 1.9, off));
      const u = this.final.uniforms['uShaftColor']!.value as Color;
      u.copy(this.shared.uSunColor.value).multiplyScalar(this.map.atmosphere.shafts * vis * 0.55);
      if (vis > 0.001) {
        const su = this.shaftPass.uniforms;
        (su['uSunUv']!.value as Vector2).set(this.sunNdc.x * 0.5 + 0.5, this.sunNdc.y * 0.5 + 0.5);
        su['uAspect']!.value = this.size.x / this.size.y;
        su['uFrame']!.value = this.frame % 64;
        this.shaftPass.render(gl, this.shaftRT);
      }
      this.final.uniforms['tShafts']!.value = this.shaftRT.texture;
    }

    if (this.quality.bloom) {
      const d0 = this.bloomDown[0]!;
      (this.prefilter.uniforms['uTexel']!.value as Vector2).set(0.5 / this.size.x, 0.5 / this.size.y);
      this.prefilter.render(gl, d0);
      for (let i = 1; i < this.bloomDown.length; i++) {
        const src = this.bloomDown[i - 1]!;
        this.down.uniforms['tInput']!.value = src.texture;
        (this.down.uniforms['uTexel']!.value as Vector2).set(1 / src.width, 1 / src.height);
        this.down.render(gl, this.bloomDown[i]!);
      }
      let input: Texture = this.bloomDown[this.bloomDown.length - 1]!.texture;
      for (let i = this.bloomDown.length - 2; i >= 0; i--) {
        const out = this.bloomUp[i]!;
        this.up.uniforms['tInput']!.value = input;
        this.up.uniforms['tBase']!.value = this.bloomDown[i]!.texture;
        (this.up.uniforms['uTexel']!.value as Vector2).set(0.5 / out.width, 0.5 / out.height);
        this.up.render(gl, out);
        input = out.texture;
      }
      this.final.uniforms['tBloom']!.value = this.bloomUp[0]!.texture;
    }

    const f = this.final.uniforms;
    // Bloom carries the colour of the light that made it (warm at golden hour).
    (f['uBloomTint']!.value as Color).copy(this.shared.uSunColor.value).lerp(WHITE, 0.8);
    (f['uResolution']!.value as Vector2).copy(this.size);
    (f['uFocus']!.value as Vector2).copy(p.focus);
    f['uSpeed']!.value = p.speed;
    f['uTime']!.value = time;
    f['uStorm']!.value = p.storm;
    f['uFlash']!.value = p.flash;
    (f['uFlashColor']!.value as Color).copy(p.flashColor);
    f['uFade']!.value = p.fade;
    f['uDesat']!.value = p.desat;
    (f['uHero']!.value as Vector3).copy(p.hero);
    this.final.render(gl, null);
  }

  /** Pre-compile every material in the scene so the first run never hitches. */
  async warm(scene: Scene, camera: Camera): Promise<void> {
    try {
      await this.gl.compileAsync(scene, camera);
    } catch {
      this.gl.compile(scene, camera);
    }
  }

  dispose(): void {
    this.sceneRT.dispose();
    this.cloudRT?.dispose();
    this.shaftRT?.dispose();
    this.shaftPass?.dispose();
    this.cloudNoise?.dispose();
    for (const t of [...this.bloomDown, ...this.bloomUp]) t.dispose();
    this.gl.dispose();
  }
}

/** Bake the tileable Perlin-Worley 3D texture on the GPU (one draw per slice, ~5 ms). */
function generateCloudNoise(gl: WebGLRenderer, size: number): WebGL3DRenderTarget {
  const rt = new WebGL3DRenderTarget(size, size, size, { depthBuffer: false });
  rt.texture.wrapS = rt.texture.wrapT = rt.texture.wrapR = RepeatWrapping;
  rt.texture.minFilter = rt.texture.magFilter = LinearFilter;
  rt.texture.generateMipmaps = false;
  const pass = new FullscreenPass(CLOUD_NOISE_FS, { uSlice: { value: 0 } });
  const prev = gl.getRenderTarget();
  for (let z = 0; z < size; z++) {
    pass.uniforms['uSlice']!.value = (z + 0.5) / size;
    pass.render(gl, rt as unknown as WebGLRenderTarget, z);
  }
  gl.setRenderTarget(prev);
  pass.dispose();
  return rt;
}

const WHITE = new Color(1, 1, 1);

function smooth(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
